import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    TextField, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Box, 
    Typography,
    Switch,
    CircularProgress,
    Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { RestaurantService } from '../../../api/services/restaurantService';
import { toast } from '../../../utils/toast';

export default function EditTableModal({ open, onClose, onSuccess, table }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [form, setForm] = useState({
        tableNumber: '',
        section: 'Indoor',
        capacity: 4,
        status: 'Available',
        isActive: true
    });

    useEffect(() => {
        if (table && open) {
            setForm({
                tableNumber: table.tableNumber || table.label || '',
                section: table.section || 'Indoor',
                capacity: table.capacity || 4,
                status: table.status || 'Available',
                isActive: table.isActive !== undefined ? table.isActive : true
            });
        }
    }, [table, open]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleClose = () => {
        setIsSubmitting(false);
        onClose();
    };

    const handleSubmit = async () => {
        if (!form.tableNumber.trim()) {
            toast.error('Table Identifier is required');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                tableNumber: form.tableNumber.trim(),
                section: form.section,
                capacity: parseInt(form.capacity, 10) || 1,
                status: form.status,
                isActive: form.isActive
            };

            await RestaurantService.updateRestaurantTable(table.id, payload);
            toast.success('Table updated successfully');
            if (onSuccess) onSuccess();
            handleClose();
        } catch (error) {
            console.error('Failed to update table:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={isSubmitting ? null : handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                <EditIcon sx={{ mr: 1 }} /> Edit Table Details
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            label="Table Identifier"
                            placeholder="e.g., T1, Booth 4"
                            value={form.tableNumber}
                            onChange={(e) => handleChange('tableNumber', e.target.value)}
                            disabled={isSubmitting}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required disabled={isSubmitting}>
                            <InputLabel>Floor Section</InputLabel>
                            <Select
                                value={form.section}
                                label="Floor Section"
                                onChange={(e) => handleChange('section', e.target.value)}
                            >
                                <MenuItem value="Indoor">Indoor</MenuItem>
                                <MenuItem value="Outdoor/Patio">Outdoor/Patio</MenuItem>
                                <MenuItem value="Bar">Bar</MenuItem>
                                <MenuItem value="VIP Lounge">VIP Lounge</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            type="number"
                            label="Seating Capacity"
                            value={form.capacity}
                            onChange={(e) => handleChange('capacity', Math.max(1, parseInt(e.target.value || 1, 10)))}
                            InputProps={{ inputProps: { min: 1, step: 1 } }}
                            disabled={isSubmitting}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required disabled={isSubmitting}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={form.status}
                                label="Status"
                                onChange={(e) => handleChange('status', e.target.value)}
                            >
                                <MenuItem value="Available">Available</MenuItem>
                                <MenuItem value="Occupied">Occupied</MenuItem>
                                <MenuItem value="Reserved">Reserved</MenuItem>
                                <MenuItem value="Dirty / Clearing">Dirty / Clearing</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight="bold">Active Directory Publish</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Publish this table to the active floor registry
                                </Typography>
                            </Box>
                            <Switch 
                                checked={form.isActive} 
                                onChange={(e) => handleChange('isActive', e.target.checked)} 
                                disabled={isSubmitting}
                                color="primary"
                            />
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                <Button onClick={handleClose} disabled={isSubmitting} color="inherit">
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    disabled={isSubmitting || !form.tableNumber.trim() || form.capacity < 1}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
